/**
 * detection-odor.js
 */

import React from 'react';
import { 
  useMediaQuery,
  FormControl, 
  FormHelperText,
  Grid, 
  Typography, 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { Types } from 'utils/constants';
import AutocompleteObject from 'components/form-inputs/autocomplete/autocomplete-object';
import DetectionExpander from './detection-expander';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import HiddenInput from 'components/form-inputs/hidden-input';
import Icon from 'components/icon';
import PhotosEdit from 'components/photos/edit';
import Select from 'components/form-inputs/select';
import TextField from 'components/form-inputs/text-field';
import { 
  sectionTypes,
  getOdorSubTypeOptions,
  getOdorQuantityUnitOptions,
  getDefaultOdorUnit,
  getOdorIds, 
  updateDefaultToCurrent 
} from './detection-common';


const useStyles = makeStyles(theme => ({
  container: {
    '& .MuiFormHelperText-root': {
      marginBottom: 0
    }
  },
  noTopPadding: {
    paddingTop: '0 !important',
  },
}));


export function OdorExpander(props) {
  const {
    id,
    recordType,
    baseName,
    isNew,
    odorIndex,
    odorEnvIndex,
    envIndex,
    odor,
    onAdd,
    onDelete,
    options,
    formContext,
    disabled,
    drawerState,
    translation
  } = props;
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.size.menuDrawer.mediaRuleMobileContent, { noSsr: true });
  const isWidthSmall = useMediaQuery(theme => theme.breakpoints.down(400), { noSsr: true });
  const isWidthVerySmall = useMediaQuery(theme => theme.breakpoints.down(360), { noSsr: true });
  const formNamePrefix = `${baseName}[${envIndex}].odorEnvironments[${odorEnvIndex}].odors[${odorIndex}]`;
  const { t: gt } = useTranslation('general');
  const { t } = useTranslation(translation);
  const { control, formState, getValues, setValue, trigger, resetField } = formContext;
  const odorType = useWatch({ control, name: `${formNamePrefix}.type`, defaultValue: odor.type });
  const odorSubType = useWatch({ control, name: `${formNamePrefix}.subType`, defaultValue: odor.subType });
  const odorTypes = React.useMemo(() =>
    Types.OdorPrimary.entries(gt, recordType === Types.Record.DEPLOYMENT
      ? [Types.OdorPrimary.PROOFING]
      : []), [gt, recordType]);
  const [odorSubTypeOptions, setOdorSubTypeOptions] = React.useState([]);
  const [odorTypeUnitOptions, setOdorTypeUnitOptions] = React.useState([]);
  const [showAdditionalPackaging, setShowAdditionalPackaging] = React.useState(false);
  const errorBase = get(formState.errors, baseName)?.[envIndex]
    ?.odorEnvironments?.[odorEnvIndex]
    ?.odors?.[odorIndex];
  const objectSuffix = recordType === Types.Record.EXERCISE ? 'Odor' : 'Seizure Incident';
  const subTitle = isNew
    ? objectSuffix
    : ((odorTypes.find(o => o.id === odorType.id)?.name ?? '') + ` ${objectSuffix}`).trim();


  // updates options and default selections for given odor type
  const onChangeOdor = React.useCallback((odorTypeId, isInit = false) => {
    const subTypeOptions = getOdorSubTypeOptions(options, odorTypeId);
    setOdorSubTypeOptions(subTypeOptions);
    if (!isInit) {
      const subType = odorTypeId === odor.type.id
        ? { id: odor.subType?.id, name: odor.subType?.name }
        : getOdorSubTypeOptions(options, odorTypeId)[0];
      setValue(`${formNamePrefix}.subType`, subType);
    }

    const unitOptions = getOdorQuantityUnitOptions(options, odorTypeId);
    setOdorTypeUnitOptions(unitOptions);
    if (!isInit) {
      setValue(`${formNamePrefix}.quantityUnit`, getDefaultOdorUnit(options, odorTypeId));
    }

    // recalculate odor ids
    if (!isNew) {
      setValue('detectionOdorIds', getOdorIds(get(getValues(), baseName)));
    }
  }, [
    options, 
    formNamePrefix, 
    baseName,
    getValues,
    setValue, 
    isNew,
    odor.type.id, 
    odor.subType?.id, 
    odor.subType?.name,
  ]);


  // resets to given values or last-saved values if given values is null
  const resetToValues = (values) => {
    resetField(`${formNamePrefix}.type`, { defaultValue: values?.type ?? undefined, keepDirty: true });
    const type = getValues(`${formNamePrefix}.type`);
    
    // update option lists
    onChangeOdor(type.id);
    
    // reset rest of fields
    resetField(`${formNamePrefix}.subType`, { defaultValue: values?.subType ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.quantity`, { defaultValue: values?.quantity ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.quantityUnit`, { defaultValue: values?.quantityUnit ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.packaging`, { defaultValue: values?.packaging ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.concealedLocation`, { defaultValue: values?.concealedLocation ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.description`, { defaultValue: values?.description ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.height`, { defaultValue: values?.height ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.depth`, { defaultValue: values?.depth ?? undefined, keepDirty: true });

    // deployment-specific
    if (recordType === Types.Record.DEPLOYMENT) {
      resetField(`${formNamePrefix}.packaging2`, { defaultValue: values?.packaging2 ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.packaging3`, { defaultValue: values?.packaging3 ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.packaging4`, { defaultValue: values?.packaging4 ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.photo`, { defaultValue: values?.photo ?? undefined, keepDirty: true });
    }
  }


  // init default odor
  React.useEffect(() => {
    onChangeOdor(odor.type.id, true);
  }, [odor.type.id, onChangeOdor]);


  // show extra packaging if extras have been saved
  React.useEffect(() => {
    if (odor.packaging2?.name || odor.packaging3?.name || odor.packaging4?.name) {
      setShowAdditionalPackaging(true);
    }
  }, [odor.packaging2, odor.packaging3, odor.packaging4]);


  // NOTE confusing:
  // if the odor is PROOFING and has been saved (has an id) then subType comes from odor.packaging
  // (Previously we did this by supplying it as defaultValue to the controller, but
  // react-hook-form v7 will only use the defaultValue if the value is undefined, not if it's
  // null, which it would be in this case, So we need to decalaratively reset the field.)
  React.useEffect(() => {
    if (odor.id && odor.type.id === Types.OdorPrimary.PROOFING) {
      resetField(`${formNamePrefix}.subType`, { defaultValue: odor.packaging });
    }
  }, [formNamePrefix, odor.id, odor.type.id, odor.packaging, resetField]);
  

  // on done 
  const onDone = async () => {
    // validate
    let validationList = [
      `${formNamePrefix}.subType`,
      `${formNamePrefix}.quantity`,
      `${formNamePrefix}.quantityUnit`,
      `${formNamePrefix}.concealedLocation`,
    ];
    if (odorType.id !== Types.OdorPrimary.PROOFING) {
      validationList.push(`${formNamePrefix}.packaging`);
    }

    const isValid = await trigger(validationList);

    if (isValid) {
      if (isNew) {
        await onAdd();
        resetToValues(odor);
      } else {
        updateDefaultToCurrent(formContext, formNamePrefix, getValues(formNamePrefix));
      }

      return true;
    }

    return false;
  }


  // on cancel
  const onCancel = () => {
    resetToValues(isNew ? odor : null);
  }


  return (
    <DetectionExpander
      id={id}
      type={sectionTypes.ODOR}
      typeTitle={subTitle}
      title={odorSubType?.name ?? ''}
      iconName='odor'
      isNew={isNew}
      onDone={onDone}
      onCancel={onCancel}
      onDelete={() => onDelete(odorIndex)}
      disabled={disabled}
      detailClassName={classes.noTopPadding}
      data-testtype='odor-expander'
      error={Boolean(
        errorBase?.subType
        || errorBase?.quantity
        || errorBase?.quantityUnitId
        || errorBase?.packaging
        || errorBase?.concealedLocation
      )}
    >
      <Typography variant='subtext-small' sx={{ mb: 2 }}>
        <Icon
          type='tip'
          fontSize='tiny'
          sx={{ color: 'comp.tip.icon', mr: 0.5 }}
        />
        <>
          TIP: Need a custom Odor TYPE or PACKAGING? Just type it in{isMobile 
            ? ` and we'll store it for next time.` 
            : `. It will automatically appear in the list next time.`}
        </>
      </Typography>

      <Grid
        container
        rowSpacing={0}
        columnSpacing={2}
        className={classes.container}
      >

        {/* odor id - hidden */}
        <HiddenInput
          formContext={formContext}
          name={`${formNamePrefix}.id`}
          defaultValue={odor.id}
          setValueAs={(value) => value ? parseInt(value) : null}
        />


        {/* odor type */}
        <Grid item xs={12} sm={shouldRenderMobileLayout() ? 6 : 12} md={6}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.odorType.helpTitle')}
            text={t('environments.odorType.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers =>
              <Select
                formContext={formContext}
                name={`${formNamePrefix}.type`}
                label={t('environments.odorType.label')}
                options={odorTypes}
                defaultValue={odor.type}
                setValueAs={id => ({ id })}
                focusHandlers={focusHandlers}
                style={{ marginTop: 4, width: '100%' }}
                onChange={e => onChangeOdor(e.target.value)}
              />}
          </HelpTextItem>
        </Grid>


        {/* odor sub type */}
        {/* {odorSubTypeOptions.length > 0 &&  */}
        <Grid item xs={12} sm={shouldRenderMobileLayout() ? 6 : 12} md={6}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.odorSubType.helpTitle')}
            text={t('environments.odorSubType.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers =>
              <AutocompleteObject
                formContext={formContext}
                name={`${formNamePrefix}.subType`}
                label={t('environments.odorSubType.label')}
                options={odorSubTypeOptions}
                focusHandlers={focusHandlers}
                defaultValue={odor.subType}
                rules={{
                  validate: val => {
                    if (!val || val.name === '' || val.name.trim() === '') return t('environments.odorSubType.errMsgEmpty');
                    else if (val.name.length > 45) return t('environments.odorSubType.errMsgLength');
                    return true;
                  }
                }}
              />}
          </HelpTextItem>
        </Grid>
        {/* } */}


        {/* quantity combo */}
        <Grid item xs={12}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.odorQuantity.helpTitle')}
            text={t('environments.odorQuantity.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers =>
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={classes.container}
                style={{ padding: '8px 0' }}
              >
                {/* quantity */}
                <Grid item
                  xs={isWidthVerySmall
                    ? 6
                    : isWidthSmall
                      ? 5
                      : 4}
                  sm={shouldRenderMobileLayout() ? 3 : 5}
                  md={4}
                  lg={3}
                >
                  <TextField
                    formContext={formContext}
                    name={`${formNamePrefix}.quantity`}
                    label={t('environments.odorQuantity.label')}
                    defaultValue={odor.quantity}
                    type="number"
                    disableErrorMessage
                    focusHandlers={focusHandlers}
                    rules={{
                      required: t('environments.odorQuantity.errMsgEmpty'),
                      validate: val =>
                        (Number(val) === parseFloat(val) && parseFloat(val) > 0)
                        || t('environments.odorQuantity.errMsgNumber')
                    }}
                  />
                </Grid>

                {/* quantity unit */}
                {odorTypeUnitOptions.length > 0 &&
                <Grid item
                  xs={isWidthVerySmall
                    ? 6
                    : isWidthSmall
                      ? 7
                      : 8}
                  sm={shouldRenderMobileLayout() ? 4 : 7}
                  md={6}
                  lg={5}
                >
                  <Select
                    formContext={formContext}
                    name={`${formNamePrefix}.quantityUnit`}
                    defaultValue={odor.quantityUnit}
                    style={{ marginTop: 4, width: '100%' }}
                    options={odorTypeUnitOptions}
                    setValueAs={id => ({ id })}
                    focusHandlers={focusHandlers}
                    disableErrorMessage
                    rules={{
                      validate: val => {
                        return (!val || val.id === '')
                          ? t('environments.odorQuantity.errMsgUnitEmpty')
                          : true;
                      }
                    }}
                  />
                </Grid>}

                {/* quantity error */}
                {Boolean(errorBase?.quantity) &&
                <Grid item xs={12} style={{ marginLeft: 14 }}>
                  <FormControl error={true}>
                    <FormHelperText>
                      {errorBase?.quantity?.message}
                    </FormHelperText>
                  </FormControl>
                </Grid>}

                {/* quantity unit error */}
                {Boolean(errorBase?.quantityUnit) &&
                <Grid item xs={12} style={{ marginLeft: 14 }}>
                  <FormControl error={true}>
                    <FormHelperText>
                      {errorBase?.quantityUnit?.message}
                    </FormHelperText>
                  </FormControl>
                </Grid>}

              </Grid>}
          </HelpTextItem>
        </Grid>


        {/* packaging types */}
        <Grid item
          xs={12}
          sm={shouldRenderMobileLayout() ? 6 : 12}
          lg={6}
          style={{ display: `${odorType && odorType.id === Types.OdorPrimary.PROOFING ? 'none' : 'block'}` }}
        >
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.odorPackaging.helpTitle')}
            text={t('environments.odorPackaging.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers =>
              <AutocompleteObject
                formContext={formContext}
                name={`${formNamePrefix}.packaging`}
                label={t('environments.odorPackaging.label')}
                defaultValue={odor.packaging}
                options={options.odorPackagingTypes}
                focusHandlers={focusHandlers}
                rules={odorType && odorType.id === Types.OdorPrimary.PROOFING
                  ? {}
                  : {
                    validate: val => {
                      if (!val || val.name === '' || val.name.trim() === '') return t('environments.odorPackaging.errMsgEmpty');
                      else if (val.name.length > 1000) return t('environments.odorPackaging.errMsgLength');
                      return true;
                    }
                  }}
              />}
          </HelpTextItem>
        </Grid>


        {/* DEPLOYMENT-ONLY additional packaging */}
        {recordType === Types.Record.DEPLOYMENT &&
        <Grid item xs={12}>
          <EditorAccordion
            name='more packaging'
            expand={showAdditionalPackaging}
            summary='More Packaging'
            detail={<Grid container spacing={2} sx={{ mt: -1, mb: 2 }}>
              {[2,3,4].map(i =>
              <React.Fragment key={i}>
                <Grid item xs={12} style={{ padding: 0 }} />
                <Grid
                  item
                  xs={12}
                  sm={shouldRenderMobileLayout() ? 6 : 12}
                  lg={6}
                >
                  <AutocompleteObject
                    formContext={formContext}
                    name={`${formNamePrefix}.packaging${i}`}
                    label={t('environments.odorPackaging.label') + ` ${i}`}
                    defaultValue={odor[`packaging${i}`]}
                    options={options.odorPackagingTypes}
                  />
                </Grid>
              </React.Fragment>
            )}
            </Grid>}
          />
        </Grid>}


        {/* concealed location */}
        <Grid item xs={12}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.concealedLocation.helpTitle')}
            text={t('environments.concealedLocation.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers =>
              <TextField
                formContext={formContext}
                name={`${formNamePrefix}.concealedLocation`}
                label={t('environments.concealedLocation.label')}
                defaultValue={odor.concealedLocation}
                focusHandlers={focusHandlers}
                rules={{
                  required: t('environments.concealedLocation.errMsgEmpty'),                  
                  validate: value => value.trim() !== '' || t('environments.concealedLocation.errMsgEmpty'),
                  maxLength: {
                    value: 500,
                    message: t('environments.concealedLocation.errMsgLength')
                  }
                }}
                multiline
                rows={2}
              />}
          </HelpTextItem>
        </Grid>


        {/* OPTIONAL - description/height/depth */}
        <Grid item xs={12}>
          <EditorAccordion
            highlight
            name="Optional"
            data-testid={`odor-optional-accordion-${odorIndex}`}
            summary={<>
              <Typography variant="body2">
                {window.innerWidth < 450
                  ? 'Optional Fields'
                  : `OPTIONAL: Description, ${recordType === Types.Record.EXERCISE
                    ? 'Height, Depth'
                    : 'Photo'}`}
              </Typography>
            </>}
            detail={<>
              <Grid 
                container
                rowSpacing={0}
                columnSpacing={2} 
                style={{ marginBottom: 0 }}
              >


                {/* odor description */}
                <Grid item xs={12}>
                  <HelpTextItem
                    sidebarText={drawerState.sidebarText}
                    title={t('environments.odorDescription.helpTitle')}
                    text={t('environments.odorDescription.helpInfo')}
                    minimalHoverStyle
                  >
                    {focusHandlers =>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.description`}
                        label={t('environments.odorDescription.label')}
                        defaultValue={odor.description}
                        focusHandlers={focusHandlers}
                        multiline
                        rows={2}
                      />}
                  </HelpTextItem>
                </Grid>


                {/* height */}
                {recordType === Types.Record.EXERCISE &&
                <Grid item xs={12} sm={6} xl={3}>
                  <HelpTextItem
                    sidebarText={drawerState.sidebarText}
                    title={t('environments.odorHeight.helpTitle')}
                    text={t('environments.odorHeight.helpInfo')}
                    minimalHoverStyle
                  >
                    {focusHandlers =>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.height`}
                        label={t('environments.odorHeight.label')}
                        defaultValue={odor.height}
                        focusHandlers={focusHandlers}
                        rules={{
                          maxLength: {
                            value: 250,
                            message: t('environments.odorHeight.errMsgLength')
                          }
                        }}
                      />}
                  </HelpTextItem>
                </Grid>}


                {/* depth */}
                {recordType === Types.Record.EXERCISE &&
                <Grid item xs={12} sm={6} xl={3}>
                  <HelpTextItem
                    sidebarText={drawerState.sidebarText}
                    title={t('environments.odorDepth.helpTitle')}
                    text={t('environments.odorDepth.helpInfo')}
                    minimalHoverStyle
                  >
                    {focusHandlers =>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.depth`}
                        label={t('environments.odorDepth.label')}
                        defaultValue={odor.depth}
                        focusHandlers={focusHandlers}
                        rules={{
                          maxLength: {
                            value: 250,
                            message: t('environments.odorDepth.errMsgLength')
                          }
                        }}
                      />}
                  </HelpTextItem>
                </Grid>}


                {/* deployment item photo */}
                {recordType === Types.Record.DEPLOYMENT &&
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <HelpTextItem
                    sidebarText={drawerState.sidebarText}
                    title={t('environments.photo.helpTitle')}
                    text={t('environments.photo.helpInfo')}
                    minimalHoverStyle
                  >
                    <PhotosEdit
                      formContext={formContext}
                      name={`${formNamePrefix}.photo`}
                      mode='single'
                      hideIcon
                      defaultValue={odor.photo}
                      drawerState={drawerState}
                      translation={translation}
                    />
                  </HelpTextItem>
                </Grid>}

              </Grid>
            </>}
          />
        </Grid>

      </Grid>

    </DetectionExpander>
  );
}
